import { useState } from 'react';
import {
  ChevronDown,
  MessageSquare,
  MessageSquarePlus,
  Trash2,
} from 'lucide-react';
import type { ActiveNote, Station } from '@hcc/types';
import { formatDateTime } from '../../lib/dateFmt';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';

/**
 * Chat-style notes panel for a person. Notes render newest-first.
 * Each note is a soft-surface bubble with a small accent stripe on the
 * left edge and a tag showing the station the note came from, so other
 * desks can tell at a glance who they need to ask about it.
 *
 * Tap "Add note" to open AddNoteModal; tap the trash icon on a note
 * to remove it (used for typos / wrong-person posts — there's no
 * "clear" semantics because notes are persistent context, not
 * actionable items).
 */
export function NotesPanel({
  notes,
  onAdd,
  onRemove,
  collapsible = false,
}: {
  notes: readonly ActiveNote[];
  onAdd: () => void;
  onRemove?: (note: ActiveNote) => void;
  // When true the body collapses behind the header so notes stay out of
  // the way at busy desks; flags carry the must-see context. Tap the
  // header to reveal. Defaults to always-open for the dialog usages.
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(!collapsible);
  const showBody = !collapsible || open;
  const HeaderTag = collapsible ? 'button' : 'div';
  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <header className="flex items-center justify-between gap-2 border-b border-divider/10 px-4 py-2.5">
        <HeaderTag
          {...(collapsible
            ? {
                type: 'button' as const,
                onClick: () => setOpen((v) => !v),
                'aria-expanded': open,
              }
            : {})}
          className={`flex items-center gap-2 ${
            collapsible ? 'cursor-pointer rounded-token-sm' : ''
          }`}
        >
          {collapsible ? (
            <ChevronDown
              className={`h-3.5 w-3.5 text-fg-muted transition-transform ${
                open ? '' : '-rotate-90'
              }`}
            />
          ) : (
            <MessageSquare className="h-3.5 w-3.5 text-accent" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
            Notes
          </span>
          <Pill tone="accent" size="sm">
            {notes.length}
          </Pill>
        </HeaderTag>
        <Button
          variant="info"
          size="sm"
          iconLeft={<MessageSquarePlus className="h-3.5 w-3.5" />}
          onClick={onAdd}
        >
          Add note
        </Button>
      </header>
      {!showBody ? null : notes.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <MessageSquare className="mx-auto h-8 w-8 text-fg-dim" />
          <p className="mt-2 text-sm font-medium text-fg">No notes yet</p>
          <p className="mt-0.5 text-xs text-fg-muted">
            Share context with other desks — accessibility, preferences,
            anything useful.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 px-3 py-3">
          {notes.map((n) => (
            <NoteRow key={n.event_id} note={n} onRemove={onRemove} />
          ))}
        </ul>
      )}
    </section>
  );
}

const STATION_LABELS: Record<Station, string> = {
  IT: 'IT',
  PPE: 'PPE / Uniform',
  Tools: 'Tools',
  Fleet: 'Fleet',
  DBSHR: 'DBS / HR',
};

function NoteRow({
  note,
  onRemove,
}: {
  note: ActiveNote;
  onRemove?: (n: ActiveNote) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const when = new Date(note.added_at);
  const author = note.added_by_name?.trim() || 'Staff';
  const initials = getInitials(author);
  const stationLabel = note.station
    ? STATION_LABELS[note.station]
    : 'Reception';
  return (
    <li className="flex gap-2.5 overflow-hidden rounded-token border border-line/10 bg-surface-2 shadow-token-sm">
      <span aria-hidden className="w-1 flex-none bg-accent" />
      <div className="flex min-w-0 flex-1 items-start gap-2.5 py-2.5 pr-2.5">
        <span
          aria-hidden
          className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent ring-1 ring-accent/20"
          title={author}
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-xs font-semibold text-fg">{author}</span>
            <Pill tone="accent" size="sm">
              {stationLabel}
            </Pill>
            <span
              className="text-[10px] tabular-nums text-fg-dim"
              title={when.toISOString()}
            >
              {formatDateTime(when)}
            </span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-fg">
            {note.text}
          </p>
        </div>
        {onRemove ? (
          confirming ? (
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => onRemove(note)}
                className="rounded-token-sm bg-danger px-2 py-1 text-[11px] font-semibold text-danger-fg hover:brightness-110"
              >
                Remove
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-[11px] font-medium text-fg-muted hover:bg-surface-2"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              aria-label="Remove note"
              className="shrink-0 rounded p-1 text-fg-dim transition-colors hover:bg-surface hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )
        ) : null}
      </div>
    </li>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
